import { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, ExternalLink, Loader2 } from 'lucide-react';

const TRIP_URL = 'https://www.collegelyceelafayette.com/london-trip';

export const TripModal = ({ open, onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="trip-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-6xl h-[92vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-slate-200 bg-white">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-lg" aria-hidden="true">🇬🇧</span>
                </div>
                <div className="min-w-0">
                  <h3 className="font-playfair font-bold text-slate-900 text-base sm:text-lg leading-tight truncate">
                    {t('tripModal.title')}
                  </h3>
                  <p className="font-inter text-xs text-slate-500 truncate">
                    {t('tripModal.subtitle')}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <a
                  href={TRIP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="trip-modal-newtab"
                  className="hidden sm:inline-flex items-center gap-1.5 font-inter text-xs font-medium text-slate-600 hover:text-amber-600 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  {t('tripModal.openNewTab')}
                </a>
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="trip-modal-close"
                  aria-label={t('tripModal.close')}
                  className="inline-flex items-center gap-1.5 font-inter text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 px-4 py-2 rounded-full transition-colors"
                >
                  <X className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('tripModal.close')}</span>
                </button>
              </div>
            </div>

            {/* Iframe + loader */}
            <div className="relative flex-1 bg-slate-100">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex flex-col items-center gap-3 text-slate-500">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                  <span className="font-inter text-sm">{t('tripModal.loading')}</span>
                </div>
              </div>
              <iframe
                title="Lafayette — Voyage à Londres"
                src={TRIP_URL}
                className="relative w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
